import * as React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import WavingHandOutlinedIcon from '@mui/icons-material/WavingHandOutlined';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import { TRUE_DARK, TRUE_MUTED } from '../auth/AuthLayout';
import { currency } from '../ui';
import { BrandColors, DEFAULT_BRAND, PublicBrand, contrast, fontStack, mix, readableOn, useFontsStylesheet } from './brand';
import QrCode from './QrCode';
import { rememberCard } from './savedCard';

type Step = { id: number; visit: number; title: string; description: string };
/** `source`: the roadmap ('visit'), signing up through an invitation ('welcome') or a friend's first purchase ('thanks'). */
type Reward = { id: number; visit: number; title: string; description: string; expires_at: string; redeemed_at: string | null; state: 'available' | 'used' | 'expired'; source?: 'visit' | 'welcome' | 'thanks' };
type InviteReward = { title: string; description: string };
/** The customer's invitation link (while the program takes invitations): how many are left and who joined. */
type Invites = {
  path: string; per_customer: number; left: number; welcome: InviteReward; thanks: InviteReward;
  friends: { first_name: string; joined_on: string; bought: boolean }[];
};
type Program = { name: string; running: boolean; starts_on: string | null; ends_on: string | null; min_amount: number; reward_days: number; total_visits: number; steps: Step[] };
/** What api/public/businesses/<slug>/mi/<token>/ publishes (Receipt-Web-Server api/loyalty.py). */
export type Card = {
  business: { name: string; slug: string; brand?: PublicBrand };
  customer: { first_name: string };
  program: Program | null;
  visits: number;
  last_visit_day: string | null;
  rewards: Reward[];
  invites?: Invites | null;
  /** Their prepaid balance, when the business uses it (Receipt-Web-Server api/balance.py). */
  balance?: CardBalance | null;
  /** The business takes orders from its catalog: the card links to it. */
  ordering?: { enabled: boolean } | null;
};
type CardBalance = {
  balance: number; paid: number; bonus: number; active: boolean; online_topup: boolean; bonus_days: number | null;
  tiers: { from: number; bonus: number }[];
  expiring: { amount: number; expires_at: string }[];
  entries: { id: number; kind: string; label: string; amount: number; created_at: string; folio: string | null }[];
  /** It also pays orders from the catalog; how the customer pays a top-up online (the business's provider). */
  orders?: boolean; online_note?: string;
};
type Tab = 'rewards' | 'wallet' | 'invite';

const SOURCE_LABEL: Record<string, string> = { welcome: 'Regalo de bienvenida', thanks: 'Por invitar a un amigo' };
const ERROR = '#C0392B';

const day = (value: string) => dayjs(value).format('D [de] MMMM');

/** A color that stands out on `background` and from the text: the brand's accent when it reads there, else a darker
 *  (or lighter) shade of it, else its primary; a brand whose accent is its text color gets a softer tone of it. */
const highlightOn = (c: BrandColors, background: string) => {
  const toward = readableOn(background) === '#FFFFFF' ? '#FFFFFF' : '#000000';
  const candidates = [c.accent, mix(c.accent, toward, 0.25), mix(c.accent, toward, 0.45), c.primary];
  const pick = candidates.find(color => contrast(color, background) >= 2.6 && contrast(color, c.text) >= 1.6);
  if (pick) return pick;
  const readable = candidates.find(color => contrast(color, background) >= 2.6) ?? mix(c.accent, toward, 0.5);
  return mix(readable, background, 0.45);
};

/** The tones ready rewards take in turn, all from the brand: its accent, its primary, a dark one, light tints and white,
 *  so several rewards don't make one wall of a single color. Repeated tones (a brand whose accent is its primary) go. */
const rewardTones = (c: BrandColors) => {
  const dark = mix(c.text, '#000000', 0.2);
  const tones = [
    { bg: c.accent, ink: c.on_accent, border: 'transparent' },
    { bg: c.primary, ink: c.on_primary, border: 'transparent' },
    { bg: mix(c.primary, c.background, 0.78), ink: c.text, border: 'transparent' },
    { bg: dark, ink: readableOn(dark), border: 'transparent' },
    { bg: mix(c.accent, c.background, 0.72), ink: c.text, border: 'transparent' },
    { bg: c.surface, ink: c.text, border: c.border },
  ];
  return tones.filter((tone, i) => tones.findIndex(other => other.bg.toUpperCase() === tone.bg.toUpperCase()) === i);
};

const REWARD_ICON = { welcome: WavingHandOutlinedIcon, thanks: PeopleAltOutlinedIcon, visit: CardGiftcardOutlinedIcon };

/** Above the card, while the business takes top-ups: saldo gives more (its best-known bonus), and it's optional —
 *  every purchase can be paid in cash or by card on the terminal as always. */
const SaldoNote = ({ balance, brand, onOpen }: { balance: CardBalance; brand: PublicBrand; onOpen: () => void }) => {
  const c = brand.colors;
  const first = balance.tiers.find(t => t.bonus > 0);
  const short = (value: number) => currency(value).replace(/\.00$/, '');
  return (
    <Box data-testid="saldo-note" sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', mb: 2, p: 1.5, borderRadius: '14px',
      bgcolor: mix(c.accent, c.background, 0.75), color: c.text }}>
      <AccountBalanceWalletOutlinedIcon sx={{ mt: 0.15, fontSize: 22, color: highlightOn(c, mix(c.accent, c.background, 0.75)) }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, fontFamily: 'inherit', lineHeight: 1.35 }}>
          {first ? `Recarga saldo y recibe más: con ${short(first.from)} te damos ${short(first.bonus)} de regalo.` : 'Recarga saldo y paga más rápido.'}
        </Typography>
        <Typography sx={{ fontSize: 13, fontFamily: 'inherit', lineHeight: 1.4, mt: 0.25 }}>
          Es opcional: también puedes pagar en efectivo o con tarjeta de débito o crédito en nuestra terminal.{' '}
          <Box component="button" type="button" onClick={onOpen}
            sx={{ p: 0, border: 0, bgcolor: 'transparent', color: 'inherit', font: 'inherit', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>
            Ver saldo
          </Box>
        </Typography>
      </Box>
    </Box>
  );
};

const pill = { textTransform: 'none', borderRadius: 99, fontWeight: 700, fontFamily: 'inherit', boxShadow: 'none' } as const;

/** The card itself, like the ones in a wallet: the business, the customer, and their saldo (or visits). Tapping it
 *  shows their code. */
const HeroCard = ({ card, brand, onShowCode }: { card: Card; brand: PublicBrand; onShowCode: () => void }) => {
  const c = brand.colors;
  const heading = { fontFamily: fontStack(brand.fonts.heading), fontStretch: brand.fonts.heading.stretch ?? 'normal' };
  // The brand's dark tone warming into its accent, like a card in a wallet (its primary is all over the page already).
  const start = mix(c.text, '#000000', 0.25);
  const end = mix(start, c.accent, 0.55);
  const ink = readableOn(mix(start, end, 0.5));
  const total = card.program?.total_visits ?? 0;
  const label = { fontSize: 11.5, letterSpacing: '0.16em', textTransform: 'uppercase', opacity: 0.72, fontFamily: 'inherit' } as const;
  return (
    <Box role="button" tabIndex={0} aria-label="Mostrar mi código" data-testid="card-hero" onClick={onShowCode}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onShowCode(); } }}
      sx={{ position: 'relative', overflow: 'hidden', cursor: 'pointer', borderRadius: '22px', p: 2.5, minHeight: 196, color: ink,
        display: 'flex', flexDirection: 'column', background: `linear-gradient(135deg, ${start} 0%, ${mix(start, end, 0.35)} 55%, ${end} 100%)`,
        boxShadow: `0 20px 40px -22px ${start}`, outline: 'none', '&:focus-visible': { boxShadow: `0 0 0 3px ${c.accent}` } }}>
      <Box aria-hidden sx={{ position: 'absolute', right: -60, top: -70, width: 220, height: 220, borderRadius: '50%', bgcolor: c.accent, opacity: 0.12 }} />
      <Box aria-hidden sx={{ position: 'absolute', right: 40, bottom: -90, width: 180, height: 180, borderRadius: '50%', bgcolor: ink, opacity: 0.05 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, position: 'relative' }}>
        <Typography sx={{ fontWeight: 800, fontSize: 19, letterSpacing: '0.12em', textTransform: 'uppercase', lineHeight: 1.2, ...heading }}>
          {card.business.name}
        </Typography>
        <Box data-testid="show-code" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, px: 1.25, py: 0.6, borderRadius: 99,
          bgcolor: ink === '#FFFFFF' ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.08)', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' }}>
          <QrCode2Icon sx={{ fontSize: 20 }} />Mi código
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 24 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, position: 'relative' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={label}>Cliente</Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 600, fontFamily: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {card.customer.first_name || 'Cliente'}
          </Typography>
        </Box>
        {card.balance ? (
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography sx={label}>Saldo</Typography>
            <Typography data-testid="card-balance-amount" sx={{ fontSize: 34, fontWeight: 800, lineHeight: 1.05, ...heading }}>{currency(card.balance.balance)}</Typography>
          </Box>
        ) : card.program ? (
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography sx={label}>Visitas</Typography>
            <Typography sx={{ fontSize: 34, fontWeight: 800, lineHeight: 1.05, ...heading }}>
              {total ? Math.min(card.visits, total) : card.visits}
              {!!total && <Box component="span" sx={{ fontSize: 18, opacity: 0.7 }}> / {total}</Box>}
            </Typography>
          </Box>
        ) : null}
      </Box>
      {card.balance && card.program && total > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, mt: 1.75, position: 'relative' }} aria-label={`${Math.min(card.visits, total)} de ${total} visitas`}>
          {Array.from({ length: total }, (_, i) => (
            <Box key={i} sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: ink, opacity: i < card.visits ? 0.9 : 0.22 }} />
          ))}
        </Box>
      )}
    </Box>
  );
};

/** What the QR on the card page says: plain text, TRUE and the card's code in capitals, letters and digits only (for
 *  scanners that don't read links, on any keyboard layout; TRUE POS's utils/card.ts reads it). A code with "-" or "_"
 *  (cards made before Oct 20, 2026, mixed case) goes in hex. Printed tickets keep the card's link in theirs. */
export const cardTextFor = (code: string) => (/^[0-9a-z]+$/i.test(code)
  ? `TRUE${code.toUpperCase()}`
  : `TRUE${Array.from(code).map(ch => ch.charCodeAt(0).toString(16).padStart(2, '0')).join('').toUpperCase()}`);

/** The code the register scans: black on white whatever the brand (what scanners read best). */
const CodeBody = ({ card, text }: { card: Card; text: string }) => (
  <Box data-testid="card-qr" sx={{ textAlign: 'center', bgcolor: '#fff', color: '#14252C', borderRadius: '20px', p: 2.5 }}>
    <QrCode text={text} size={232} />
    <Typography sx={{ fontWeight: 800, fontSize: 18, mt: 1.5, fontFamily: 'inherit' }}>{card.customer.first_name || 'Tu código'}</Typography>
    <Typography sx={{ fontWeight: 700, mt: 0.5, fontFamily: 'inherit' }}>Muéstralo en caja</Typography>
    <Typography sx={{ color: '#5B6B73', fontSize: 13.5, fontFamily: 'inherit' }}>
      Lo escanean y listo: no tienes que dar tu teléfono{card.balance ? '. Así también pagas con tu saldo.' : '.'}
    </Typography>
  </Box>
);

/** The customer's prepaid balance, like a wallet: the more they top up, the more they get (the business's tiers, to
 *  pick from), topping up online, what expires and their movements. */
const WalletSection = ({ card, brand, slug, token }: { card: Card; brand: PublicBrand; slug?: string; token?: string }) => {
  const balance = card.balance!;
  const c = brand.colors;
  const heading = { fontFamily: fontStack(brand.fonts.heading), fontStretch: brand.fonts.heading.stretch ?? 'normal' };
  const tiers = balance.tiers.filter(t => t.from > 0);
  const withBonus = tiers.some(t => t.bonus > 0);
  const amounts = tiers.length ? tiers.map(t => t.from) : [100, 200, 500];
  const [amount, setAmount] = React.useState<number>(amounts[0]);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const bonusOf = (value: number) => balance.tiers.filter(t => value >= t.from).reduce((best, t) => Math.max(best, t.bonus), 0);
  const bonus = bonusOf(amount);
  const highlight = highlightOn(c, c.background);
  const dark = mix(c.text, '#000000', 0.25);
  const onDark = highlightOn(c, dark);
  const quiet = mix(c.text, c.background, 0.55);
  const [low, high] = [Math.min(...amounts), Math.max(...amounts)];
  const barHeight = (value: number) => (high === low ? 70 : 30 + (70 * (value - low)) / (high - low));
  const firstBonus = tiers.find(t => t.bonus > 0);

  const topup = async () => {
    if (!slug || !token) return;
    setBusy(true);
    setError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/public/businesses/${encodeURIComponent(slug)}/mi/${encodeURIComponent(token)}/topup/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ amount }),
      });
      const body = await response.json().catch(() => ({}));
      if (response.ok && body.url) { window.location.href = body.url; return; }
      setError(body.detail || 'No se pudo abrir el pago. Intenta de nuevo.');
    } catch {
      setError('No hay conexión. Vuelve a intentar.');
    }
    setBusy(false);
  };

  return (
    <Box component="section" data-testid="card-balance">
      {balance.active && (
        <>
          <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: 27, sm: 30 }, lineHeight: 1.12, ...heading }}>
            {withBonus ? <>Entre más recargas,<br /><Box component="span" sx={{ color: highlight }}>más recibes.</Box></>
              : <>Recarga tu saldo<br /><Box component="span" sx={{ color: highlight }}>y paga más rápido.</Box></>}
          </Typography>

          {withBonus ? (
            <Box role="radiogroup" aria-label="Monto de la recarga" sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', height: 132 }}>
                {amounts.map(value => {
                  const selected = value === amount;
                  return (
                    <Box key={value} role="radio" aria-checked={selected} tabIndex={0} data-testid={`topup-${value}`} onClick={() => setAmount(value)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAmount(value); } }}
                      sx={{ flex: 1, height: `${barHeight(value)}%`, borderRadius: '14px', cursor: 'pointer', outline: 'none', transition: 'background-color 160ms, box-shadow 160ms',
                        bgcolor: selected ? dark : quiet, boxShadow: selected ? `0 0 0 2px ${highlight}, 0 10px 20px -12px ${dark}` : 'none',
                        '&:focus-visible': { boxShadow: `0 0 0 3px ${highlight}` } }} />
                  );
                })}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1.25 }}>
                {amounts.map(value => (
                  <Box key={value} onClick={() => setAmount(value)} sx={{ flex: 1, textAlign: 'center', cursor: 'pointer', minWidth: 0 }}>
                    <Typography sx={{ fontFamily: 'inherit', fontSize: 15, fontWeight: value === amount ? 800 : 500, color: value === amount ? c.text : c.muted, whiteSpace: 'nowrap' }}>
                      {currency(value).replace(/\.00$/, '')}
                    </Typography>
                    <Typography sx={{ fontFamily: 'inherit', fontSize: 13, color: bonusOf(value) ? highlight : c.muted, mt: 0.25 }}>
                      {bonusOf(value) ? `+${currency(bonusOf(value)).replace(/\.00$/, '')}` : '—'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2.5 }} role="radiogroup" aria-label="Monto de la recarga">
              {amounts.map(value => (
                <Button key={value} role="radio" aria-checked={value === amount} onClick={() => setAmount(value)} data-testid={`topup-${value}`}
                  variant={value === amount ? 'contained' : 'outlined'}
                  sx={{ ...pill, flex: 1, py: 1, ...(value === amount ? { bgcolor: dark, color: '#fff', '&:hover': { bgcolor: dark, boxShadow: 'none' } }
                    : { color: c.text, borderColor: c.border }) }}>
                  {currency(value).replace(/\.00$/, '')}
                </Button>
              ))}
            </Box>
          )}

          {withBonus && (
            <Box data-testid="topup-bonus" sx={{ mt: 3, p: 3, borderRadius: '22px', bgcolor: dark, color: '#fff', textAlign: 'center' }}>
              {bonus > 0 ? (
                <>
                  <Typography sx={{ fontSize: 12.5, letterSpacing: '0.2em', textTransform: 'uppercase', color: onDark, fontFamily: 'inherit' }}>Tu regalo de recarga</Typography>
                  <Typography sx={{ fontSize: 46, fontWeight: 800, lineHeight: 1.15, color: onDark, ...heading }}>+{currency(bonus).replace(/\.00$/, '')}</Typography>
                  <Typography sx={{ opacity: 0.75, fontFamily: 'inherit' }}>al recargar {currency(amount).replace(/\.00$/, '')}</Typography>
                </>
              ) : (
                <Typography sx={{ opacity: 0.85, fontFamily: 'inherit' }}>
                  {firstBonus ? `Recarga ${currency(firstBonus.from).replace(/\.00$/, '')} o más y recibe +${currency(firstBonus.bonus).replace(/\.00$/, '')} de regalo.` : 'Este monto no trae regalo.'}
                </Typography>
              )}
              {bonus > 0 && balance.bonus_days && (
                <Typography sx={{ opacity: 0.6, fontSize: 12.5, mt: 1, fontFamily: 'inherit' }}>El regalo se usa en {balance.bonus_days} días.</Typography>
              )}
            </Box>
          )}

          {balance.online_topup ? (
            <>
              <Button onClick={topup} disabled={busy} fullWidth variant="contained" data-testid="topup-online"
                sx={{ ...pill, mt: 2.5, py: 1.6, fontSize: 17, color: readableOn(c.accent), background: `linear-gradient(90deg, ${mix(c.accent, '#000000', 0.12)}, ${mix(c.accent, '#FFFFFF', 0.28)})`,
                  '&:hover': { boxShadow: 'none', opacity: 0.94 }, '&.Mui-disabled': { color: readableOn(c.accent), opacity: 0.7 } }}>
                {busy ? 'Abriendo…' : `Recargar ${currency(amount).replace(/\.00$/, '')}${bonus ? ` · recibe ${currency(bonus).replace(/\.00$/, '')} extra` : ''}`}
              </Button>
              <Typography sx={{ color: c.muted, fontSize: 12.5, mt: 1, textAlign: 'center', fontFamily: 'inherit' }}>
                {balance.online_note || 'Pagas en línea con tarjeta.'} El saldo te llega al instante.
              </Typography>
              {error && <Typography role="alert" sx={{ color: ERROR, fontSize: 14, mt: 1, textAlign: 'center', fontFamily: 'inherit' }}>{error}</Typography>}
            </>
          ) : (
            <Box sx={{ mt: 2.5, p: 1.75, borderRadius: '16px', bgcolor: c.surface, border: `1px solid ${c.border}`, textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 700, fontFamily: 'inherit' }}>Recarga en caja</Typography>
              <Typography sx={{ fontSize: 14, color: c.muted, fontFamily: 'inherit' }}>Dile al cajero cuánto quieres recargar en tu próxima visita.</Typography>
            </Box>
          )}
        </>
      )}

      <Box sx={{ mt: balance.active ? 3 : 0, p: 2, borderRadius: '16px', bgcolor: c.surface, border: `1px solid ${c.border}` }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
          <Typography sx={{ fontWeight: 700, fontFamily: 'inherit' }}>Tu saldo</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: 20, ...heading }}>{currency(balance.balance)}</Typography>
        </Box>
        {balance.bonus > 0 && <Typography sx={{ fontSize: 13.5, color: c.muted, fontFamily: 'inherit' }}>Incluye {currency(balance.bonus)} de regalo.</Typography>}
        {balance.expiring.map((lot, i) => (
          <Typography key={i} sx={{ fontSize: 13.5, color: highlight, fontFamily: 'inherit' }}>{currency(lot.amount)} de regalo vence el {day(lot.expires_at)}.</Typography>
        ))}
        <Typography sx={{ fontSize: 13.5, mt: 0.75, fontFamily: 'inherit' }}>
          Úsalo en caja mostrando tu código{balance.orders && card.ordering?.enabled ? ', o en tus pedidos en línea desde este teléfono' : ''}.
        </Typography>
      </Box>

      {balance.entries.length > 0 && (
        <Box sx={{ mt: 2.5 }}>
          <Typography component="h3" sx={{ fontWeight: 800, fontSize: 17, mb: 1, ...heading }}>Movimientos</Typography>
          <Box sx={{ borderRadius: '16px', bgcolor: c.surface, border: `1px solid ${c.border}`, overflow: 'hidden' }}>
            {balance.entries.map((entry, i) => (
              <Box key={entry.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, px: 1.75, py: 1.1, borderTop: i ? `1px solid ${c.border}` : 'none' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14.5, fontWeight: 600, fontFamily: 'inherit' }}>{entry.label}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: c.muted, fontFamily: 'inherit' }}>{dayjs(entry.created_at).format('D MMM YYYY')}</Typography>
                </Box>
                <Typography sx={{ fontWeight: 700, fontFamily: 'inherit', flexShrink: 0, color: entry.amount > 0 ? highlight : c.text }}>
                  {entry.amount > 0 ? '+' : '−'}{currency(Math.abs(entry.amount))}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

/** The customer's invitation: what each side gets, the link to share (WhatsApp or copied) and the friends who joined. */
const InviteSection = ({ card, brand }: { card: Card; brand: PublicBrand }) => {
  const invites = card.invites!;
  const c = brand.colors;
  const heading = { fontFamily: fontStack(brand.fonts.heading), fontStretch: brand.fonts.heading.stretch ?? 'normal' };
  const url = `${window.location.origin}${invites.path}`;
  const [copied, setCopied] = React.useState(false);
  const message = `Te invito a ${card.business.name}: regístrate con mi enlace y llévate ${invites.welcome.title}. ${url}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      window.prompt('Copia tu enlace:', url);
    }
  };
  return (
    <Box component="section" data-testid="card-invites">
      <Typography component="h2" sx={{ fontWeight: 800, fontSize: 26, lineHeight: 1.15, mb: 1.5, ...heading }}>
        Invita a un amigo<br /><Box component="span" sx={{ color: highlightOn(c, c.background) }}>y ganan los dos.</Box>
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
        {[{ who: 'Tu amigo', reward: invites.welcome, when: 'al registrarse con tu enlace' }, { who: 'Tú', reward: invites.thanks, when: 'cuando haga su primera compra' }].map(side => (
          <Box key={side.who} sx={{ p: 1.75, borderRadius: '16px', bgcolor: c.surface, border: `1px solid ${c.border}` }}>
            <Typography sx={{ fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', color: c.muted, fontFamily: 'inherit' }}>{side.who}</Typography>
            <Typography sx={{ fontWeight: 800, lineHeight: 1.25, mt: 0.5, ...heading }}>{side.reward.title}</Typography>
            <Typography sx={{ fontSize: 13, color: c.muted, mt: 0.5, fontFamily: 'inherit' }}>{side.when}</Typography>
          </Box>
        ))}
      </Box>
      {invites.left > 0 ? (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2.5 }}>
            <Button variant="contained" startIcon={<WhatsAppIcon />} href={`https://wa.me/?text=${encodeURIComponent(message)}`} target="_blank" rel="noopener"
              sx={{ ...pill, flex: 1, py: 1.1, bgcolor: c.primary, color: c.on_primary, '&:hover': { bgcolor: c.primary, boxShadow: 'none', opacity: 0.9 } }}>
              Compartir por WhatsApp
            </Button>
            <Button variant="outlined" onClick={copy} sx={{ ...pill, py: 1.1, color: c.text, borderColor: c.border }}>
              {copied ? 'Enlace copiado' : 'Copiar enlace'}
            </Button>
          </Box>
          <Typography sx={{ color: c.muted, fontSize: 13, mt: 1, fontFamily: 'inherit' }}>
            Te {invites.left === 1 ? 'queda 1 invitación' : `quedan ${invites.left} invitaciones`} de {invites.per_customer}. Solo cuentan amigos nuevos en {card.business.name}.
          </Typography>
        </>
      ) : (
        <Typography sx={{ color: c.muted, fontSize: 13.5, mt: 1.5, fontFamily: 'inherit' }}>Ya usaste tus {invites.per_customer} invitaciones. ¡Gracias por recomendarnos!</Typography>
      )}
      {invites.friends.length > 0 && (
        <Box sx={{ mt: 2.5 }}>
          <Typography component="h3" sx={{ fontWeight: 800, fontSize: 17, mb: 1, ...heading }}>Tus amigos</Typography>
          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, borderRadius: '16px', bgcolor: c.surface, border: `1px solid ${c.border}`, overflow: 'hidden' }}>
            {invites.friends.map((friend, i) => (
              <Box component="li" key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, px: 1.75, py: 1.1, borderTop: i ? `1px solid ${c.border}` : 'none', fontSize: 14 }}>
                <Box component="span" sx={{ fontWeight: 600 }}>{friend.first_name}</Box>
                <Box component="span" sx={{ color: friend.bought ? c.text : c.muted, textAlign: 'right' }}>
                  {friend.bought ? 'Ya compró: ganaste tu recompensa' : `Se registró el ${day(friend.joined_on)}`}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

/** What the customer's next visits bring: the first step past their visits whose reward they haven't used or let
 * expire (one used early, on a day that didn't count as a visit, is behind them). */
const upcomingStep = (card: Card) =>
  card.program?.steps.find(step => step.visit > card.visits && !card.rewards.some(r => r.visit === step.visit && r.state !== 'available'));

/** Where a step of the roadmap stands for this customer. */
const stepState = (step: Step, card: Card, upcoming?: Step) => {
  const reward = card.rewards.find(r => r.visit === step.visit);
  if (reward?.state === 'available') return { label: 'Lista para usar', tone: 'ready' as const };
  if (reward?.state === 'used') return { label: `Usada el ${day(reward.redeemed_at!)}`, tone: 'done' as const };
  if (reward?.state === 'expired') return { label: 'Venció', tone: 'past' as const };
  if (card.visits >= step.visit) return { label: 'Hecho', tone: 'done' as const };
  if (step.visit === upcoming?.visit) return { label: 'Sigue', tone: 'next' as const };
  return null;
};

/** Rewards by visits: what they can redeem now first, then their progress, the roadmap and the rules. */
/** Where the customer orders with their card: the business's own website (the card goes in the link's #, which no
 * server sees; that site keeps it on this phone) or its TRUE catalog, where this page already kept it. */
export type OrderTarget = { href: string } | { to: string };

const OrderButton = ({ target, children, sx, testId }: { target: OrderTarget; children: React.ReactNode; sx: object; testId: string }) =>
  'href' in target
    ? <Button component="a" href={target.href} startIcon={<ShoppingBagOutlinedIcon />} variant="contained" data-testid={testId} sx={sx}>{children}</Button>
    : <Button component={Link} to={target.to} startIcon={<ShoppingBagOutlinedIcon />} variant="contained" data-testid={testId} sx={sx}>{children}</Button>;

const RewardsSection = ({ card, brand, orderTarget }: { card: Card; brand: PublicBrand; orderTarget?: OrderTarget | null }) => {
  const c = brand.colors;
  const heading = { fontFamily: fontStack(brand.fonts.heading), fontStretch: brand.fonts.heading.stretch ?? 'normal' };
  const program = card.program!;
  const total = program.total_visits ?? 0;
  // Signed up through an invitation and not bought yet: their next visit is their first (with the welcome gift).
  const first = card.visits === 0;
  const next = first ? undefined : upcomingStep(card);
  const ready = card.rewards.filter(r => r.state === 'available');
  const tones = rewardTones(c);
  const roadmap: Step[] = [{ id: 0, visit: 1, title: 'Registro', description: 'Te registras con tu teléfono al pagar.' }, ...program.steps];
  return (
    <Box component="section">
      <Box data-testid="card-rewards">
        <Typography component="h2" sx={{ fontWeight: 800, fontSize: 20, mb: 1, ...heading }}>Recompensas por canjear</Typography>
        {ready.length === 0 ? (
          <Typography sx={{ color: c.muted, fontSize: 14.5, fontFamily: 'inherit' }}>Por ahora no tienes recompensas por canjear.</Typography>
        ) : ready.map((reward, i) => {
          const tone = tones[i % tones.length];
          const Icon = REWARD_ICON[reward.source ?? 'visit'] ?? CardGiftcardOutlinedIcon;
          return (
          <Box key={reward.id} data-testid="ready-reward"
            sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 2.25, mb: 1.75, borderRadius: '18px', bgcolor: tone.bg, color: tone.ink, border: `1px solid ${tone.border}` }}>
            <Icon sx={{ mt: 0.25, fontSize: 28 }} />
            <Box sx={{ minWidth: 0 }}>
              {reward.source && SOURCE_LABEL[reward.source] && (
                <Typography sx={{ fontSize: 12, fontWeight: 700, opacity: 0.8, textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: 'inherit' }}>{SOURCE_LABEL[reward.source]}</Typography>
              )}
              <Typography sx={{ fontWeight: 800, fontSize: 19, lineHeight: 1.25, ...heading }}>{reward.title}</Typography>
              {reward.description && <Typography sx={{ fontSize: 14, opacity: 0.85, fontFamily: 'inherit' }}>{reward.description}</Typography>}
              <Typography sx={{ fontSize: 13, mt: 0.75, fontFamily: 'inherit' }}>
                Canjéala en caja antes del {day(reward.expires_at)}: muestra tu código o da tu teléfono.
                {card.ordering?.enabled ? ' También en tus pedidos en línea, pidiendo desde esta tarjeta.' : ''}
              </Typography>
            </Box>
          </Box>
          );
        })}
        {ready.length > 0 && orderTarget && (
          <OrderButton target={orderTarget} testId="card-order-rewards"
            sx={{ mt: 0.5, mb: 1.5, borderRadius: 99, textTransform: 'none', fontWeight: 700, fontFamily: 'inherit', boxShadow: 'none',
              bgcolor: c.primary, color: c.on_primary, '&:hover': { bgcolor: c.primary, boxShadow: 'none', opacity: 0.92 } }}>
            {ready.length === 1 ? 'Úsala en un pedido en línea' : 'Úsalas en un pedido en línea'}
          </OrderButton>
        )}
      </Box>

      <Box data-testid="card-progress" sx={{ mt: 2, p: 2, borderRadius: '16px', bgcolor: c.surface, border: `1px solid ${c.border}` }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15, fontFamily: 'inherit' }}>{program.name}</Typography>
          <Typography sx={{ fontWeight: 600, fontSize: 14, color: c.muted, fontFamily: 'inherit', flexShrink: 0 }}>
            {total ? `${Math.min(card.visits, total)} de ${total} visitas` : `${card.visits} ${card.visits === 1 ? 'visita' : 'visitas'}`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }} aria-hidden>
          {Array.from({ length: total }, (_, i) => (
            <Box key={i} sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: i < card.visits ? c.primary : c.border }} />
          ))}
        </Box>
        <Typography sx={{ mt: 1, fontSize: 13.5, color: c.muted, fontFamily: 'inherit' }}>
          {!program.running
            ? 'El programa no está activo ahora.'
            : first ? 'Tu primera compra cuenta como tu visita 1.'
              : next ? <>Siguiente: visita {next.visit} · {next.title}</> : total ? '¡Completaste la ruta!' : 'Pronto habrá recompensas por tus visitas.'}
        </Typography>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography component="h2" sx={{ fontWeight: 800, fontSize: 18, mb: 1, ...heading }}>Tu ruta</Typography>
        <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, bgcolor: c.surface, border: `1px solid ${c.border}`, borderRadius: '16px', overflow: 'hidden' }}>
          {roadmap.map((step, i) => {
            const state = step.visit === 1
              ? (card.visits >= 1 ? { label: 'Hecho', tone: 'done' as const } : { label: 'Sigue', tone: 'next' as const })
              : stepState(step, card, next);
            const reached = state?.tone === 'done' || state?.tone === 'ready';
            return (
              <Box component="li" key={step.visit} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', p: 1.75, borderTop: i ? `1px solid ${c.border}` : 'none', bgcolor: state?.tone === 'ready' ? `${c.accent}14` : 'transparent' }}>
                <Box sx={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 14,
                  bgcolor: reached ? c.primary : 'transparent', color: reached ? c.on_primary : c.text, border: `2px solid ${reached ? c.primary : c.border}` }}>
                  {state?.tone === 'done' ? <CheckIcon sx={{ fontSize: 18 }} /> : step.visit}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 15, fontFamily: 'inherit' }}>Visita {step.visit} · {step.title}</Typography>
                  {step.description && <Typography sx={{ color: c.muted, fontSize: 13.5, fontFamily: 'inherit' }}>{step.description}</Typography>}
                  {state?.tone === 'done' && state.label !== 'Hecho' && (
                    <Typography sx={{ fontSize: 12.5, color: c.muted, fontFamily: 'inherit' }}>{state.label}</Typography>
                  )}
                </Box>
                {state && state.tone !== 'done' && (
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, flexShrink: 0, color: state.tone === 'past' ? c.muted : c.text, fontFamily: 'inherit' }}>{state.label}</Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ mt: 3, color: c.muted, fontSize: 13.5, lineHeight: 1.6 }}>
        <Typography component="h2" sx={{ fontWeight: 700, fontSize: 14, color: c.text, fontFamily: 'inherit' }}>Cómo funciona</Typography>
        <Box component="ul" sx={{ pl: 2.5, my: 0.5 }}>
          <li>Cuenta una visita por día{program.min_amount > 0 ? `, con compra de ${currency(program.min_amount)} o más` : ' con tu compra'}. Al pagar, muestra tu código o da tu teléfono.</li>
          <li>Cada recompensa es personal y vale {program.reward_days} días. Se usa una por compra.</li>
          {program.ends_on && <li>El programa termina el {day(program.ends_on)}.</li>}
        </Box>
      </Box>
    </Box>
  );
};

/** The customer's card in the business's brand: the card with their saldo or visits and their code, then one section
 *  at a time (rewards by visits, the wallet, invitations) instead of everything on one long page. */
export const CardView = ({ card, embedded, cardUrl, welcome, slug, token, notice }: {
  card: Card; embedded?: boolean; cardUrl?: string; welcome?: boolean; slug?: string; token?: string; notice?: string;
}) => {
  const brand = card.business.brand ?? DEFAULT_BRAND;
  const c = brand.colors;
  const heading = { fontFamily: fontStack(brand.fonts.heading), fontStretch: brand.fonts.heading.stretch ?? 'normal' };
  const program = card.program;
  const ready = card.rewards.filter(r => r.state === 'available').length;
  const tabs: { id: Tab; label: string; badge?: number }[] = [
    ...(program ? [{ id: 'rewards' as const, label: 'Recompensas', badge: ready }] : []),
    ...(card.balance ? [{ id: 'wallet' as const, label: 'Saldo' }] : []),
    ...(card.invites ? [{ id: 'invite' as const, label: 'Invitar' }] : []),
  ];
  const [tab, setTab] = React.useState<Tab | undefined>(() => (notice && card.balance ? 'wallet' : ready ? 'rewards' : tabs[0]?.id));
  React.useEffect(() => { if (notice && card.balance) setTab('wallet'); }, [notice, card.balance]);
  const current = tabs.find(t => t.id === tab)?.id ?? tabs[0]?.id;
  const [showCode, setShowCode] = React.useState(false);
  const codeText = cardTextFor(token || cardUrl?.split('/mi/').pop() || '');
  const code = token || cardUrl?.split('/mi/').pop() || '';
  const orderTarget: OrderTarget | null = !card.ordering?.enabled ? null
    : brand.order_url && code ? { href: `${brand.order_url.split('#')[0]}#tarjeta=${encodeURIComponent(code)}` }
      : { to: `/${card.business.slug}` };

  return (
    <Box sx={{ minHeight: embedded ? '100%' : '100vh', bgcolor: c.background, color: c.text, fontFamily: fontStack(brand.fonts.body), fontStretch: brand.fonts.body.stretch ?? 'normal' }}>
      <Box component="header" sx={{ maxWidth: 560, mx: 'auto', px: 2, pt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        {brand.logo
          ? <Box component="img" src={brand.logo} alt={card.business.name} sx={{ height: 40, maxWidth: 160, objectFit: 'contain' }} />
          : <Typography sx={{ fontWeight: 800, fontSize: 17, ...heading }}>{card.business.name}</Typography>}
        {orderTarget ? (
          <OrderButton target={orderTarget} testId="card-order"
            sx={{ ...pill, py: 0.9, px: 2.25, bgcolor: c.accent, color: c.on_accent, '&:hover': { bgcolor: c.accent, boxShadow: 'none', opacity: 0.92 } }}>
            Pedir
          </OrderButton>
        ) : (
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: c.muted, fontFamily: 'inherit' }}>Mi tarjeta</Typography>
        )}
      </Box>

      <Box component="main" sx={{ maxWidth: 560, mx: 'auto', px: 2, pt: 2, pb: 3 }}>
        <Typography component="h1" sx={{ fontWeight: 800, fontSize: 24, mb: 1.5, ...heading }}>Hola, {card.customer.first_name || 'cliente'}</Typography>
        {card.balance?.active && <SaldoNote balance={card.balance} brand={brand} onOpen={() => {
          setTab('wallet');
          window.setTimeout(() => document.querySelector('[data-testid="card-balance"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
        }} />}
        <HeroCard card={card} brand={brand} onShowCode={() => setShowCode(true)} />

        {embedded && showCode && cardUrl && (
          <Box sx={{ mt: 2, border: `1px solid ${c.border}`, borderRadius: '20px' }}><CodeBody card={card} text={codeText} /></Box>
        )}

        {welcome && (
          <Box data-testid="card-welcome" sx={{ mt: 2, p: 2, borderRadius: '16px', bgcolor: c.accent, color: c.on_accent }}>
            <Typography sx={{ fontWeight: 700, fontFamily: 'inherit' }}>¡Listo, ya tienes tu tarjeta!</Typography>
            <Typography sx={{ fontSize: 14, fontFamily: 'inherit' }}>Guarda este enlace. En caja, muestra tu código o da tu teléfono, y usa tu regalo de bienvenida.</Typography>
          </Box>
        )}
        {notice && (
          <Box data-testid="card-notice" sx={{ mt: 2, p: 2, borderRadius: '16px', bgcolor: c.accent, color: c.on_accent }}>
            <Typography sx={{ fontWeight: 700, fontFamily: 'inherit' }}>{notice}</Typography>
          </Box>
        )}

        {tabs.length > 1 && (
          <Box role="tablist" aria-label="Secciones de tu tarjeta"
            sx={{ display: 'flex', gap: 0.75, mt: 3.5, p: 0.75, borderRadius: 99, bgcolor: mix(c.text, c.background, 0.9) }}>
            {tabs.map(t => {
              const selected = t.id === current;
              return (
                <Button key={t.id} role="tab" aria-selected={selected} data-testid={`card-tab-${t.id}`} onClick={() => setTab(t.id)}
                  sx={{ ...pill, flex: '1 1 auto', py: 1, px: { xs: 1.25, sm: 2 }, minWidth: 0, whiteSpace: 'nowrap', fontSize: { xs: 13.5, sm: 14.5 }, color: selected ? c.text : c.muted,
                    bgcolor: selected ? c.surface : 'transparent', boxShadow: selected ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                    '&:hover': { bgcolor: selected ? c.surface : 'rgba(255,255,255,0.45)', boxShadow: selected ? '0 2px 8px rgba(0,0,0,0.08)' : 'none' } }}>
                  {t.label}
                  {!!t.badge && (
                    <Box component="span" sx={{ ml: 0.75, minWidth: 19, height: 19, px: 0.5, borderRadius: 99, display: 'inline-grid', placeItems: 'center', fontSize: 11.5, flexShrink: 0,
                      bgcolor: c.accent, color: c.on_accent }}>{t.badge}</Box>
                  )}
                </Button>
              );
            })}
          </Box>
        )}

        <Box sx={{ mt: 3.5 }} role={tabs.length > 1 ? 'tabpanel' : undefined}>
          {current === 'rewards' && <RewardsSection card={card} brand={brand} orderTarget={embedded ? null : orderTarget} />}
          {current === 'wallet' && <WalletSection card={card} brand={brand} slug={slug} token={token} />}
          {current === 'invite' && <InviteSection card={card} brand={brand} />}
          {!program && !card.balance && (
            <Typography sx={{ color: c.muted, fontFamily: 'inherit' }}>{card.business.name} no tiene un programa de visitas por ahora.</Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: 13, mt: 3, color: c.muted, fontFamily: 'inherit' }}>Guarda este enlace: es tu tarjeta.</Typography>
      </Box>

      <Box component="footer" sx={{ maxWidth: 560, mx: 'auto', px: 2, pb: 5, pt: 2, display: 'flex', justifyContent: 'center', borderTop: `1px solid ${c.border}` }}>
        <Typography component={Link} to={`/${card.business.slug}`} sx={{ color: c.muted, fontSize: 13, textDecoration: 'none', fontFamily: 'inherit' }}>
          {card.business.name} · Hecho con TRUE POS
        </Typography>
        {brand.privacy_url && (
          <Typography component="a" href={brand.privacy_url} target="_blank" rel="noopener" sx={{ color: c.muted, fontSize: 13, ml: 2, fontFamily: 'inherit' }}>
            Aviso de privacidad
          </Typography>
        )}
      </Box>

      {!embedded && cardUrl && (
        <Drawer anchor="bottom" open={showCode} onClose={() => setShowCode(false)}
          slotProps={{ paper: { sx: { borderTopLeftRadius: 24, borderTopRightRadius: 24, bgcolor: c.background, color: c.text, maxWidth: 560, mx: 'auto' } } }}>
          <Box sx={{ p: 2, pb: 4, fontFamily: fontStack(brand.fonts.body) }} data-testid="code-sheet">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 18, ...heading }}>Tu código</Typography>
              <IconButton onClick={() => setShowCode(false)} aria-label="Cerrar" sx={{ color: c.text }}><CloseIcon /></IconButton>
            </Box>
            <CodeBody card={card} text={codeText} />
          </Box>
        </Drawer>
      )}
    </Box>
  );
};

/** receipt.com.mx/<slug>/mi/<token>: the customer's own card, the link on their ticket and WhatsApp receipt. */
const LoyaltyCard = () => {
  const { slug = '', token = '' } = useParams();
  // Just signed up through an invitation (InvitePage).
  const location = useLocation();
  const navigate = useNavigate();
  const welcome = !!(location.state as { welcome?: boolean } | null)?.welcome;
  // Back from topping up online: ?status=approved|rejected|… on this page's address (Mercado Pago's).
  const [notice, setNotice] = React.useState('');
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const result = params.get('status') ?? params.get('collection_status');
    if (!result) return;
    setNotice(result === 'approved' ? '¡Recarga recibida! Tu saldo ya está actualizado.' : 'Tu pago no se completó: no se te cobró. Puedes intentar de nuevo.');
    navigate(location.pathname, { replace: true });
  }, [location.search, location.pathname, navigate]);
  const [card, setCard] = React.useState<Card | null>(null);
  const [state, setState] = React.useState<'loading' | 'found' | 'missing'>('loading');

  React.useEffect(() => {
    let alive = true;
    fetch(`${process.env.REACT_APP_BASE_URL}/api/public/businesses/${encodeURIComponent(slug)}/mi/${encodeURIComponent(token)}/`)
      .then(async response => {
        if (!alive) return;
        if (!response.ok) { setState('missing'); return; }
        setCard(await response.json());
        setState('found');
        // On this phone from now on: the business's catalog offers paying orders with their saldo.
        rememberCard(slug, token);
      })
      .catch(() => { if (alive) setState('missing'); });
    return () => { alive = false; };
  }, [slug, token]);

  useFontsStylesheet(card?.business.brand?.fonts_css);

  React.useEffect(() => {
    // A personal page: kept out of search engines.
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex';
    document.head.appendChild(robots);
    return () => { robots.remove(); };
  }, []);

  React.useEffect(() => {
    document.title = card ? `${card.business.name} · Mi tarjeta` : 'TRUE POS';
  }, [card]);

  if (state !== 'found' || !card) {
    return (
      <>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', bgcolor: '#F2F5F7', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2, py: 6, fontFamily: 'Inter, sans-serif' }}>
          <Box sx={{ width: '100%', maxWidth: 440, bgcolor: '#fff', borderRadius: '20px', boxShadow: '0 8px 30px rgba(20,37,44,0.08)', p: { xs: 4, sm: 5 }, textAlign: 'center' }}>
            {state === 'loading' && <CircularProgress size={32} sx={{ color: TRUE_DARK, my: 6 }} />}
            {state === 'missing' && (
              <>
                <Typography component="h1" sx={{ fontWeight: 700, fontSize: 24, color: TRUE_DARK }}>No encontramos esta tarjeta</Typography>
                <Typography sx={{ color: TRUE_MUTED, mt: 1.5 }}>Revisa el enlace de tu ticket, o pide tu tarjeta en tu próxima visita.</Typography>
              </>
            )}
          </Box>
          <Typography component={Link} to="/" sx={{ mt: 3, color: TRUE_MUTED, fontSize: 13, textDecoration: 'none' }}>Hecho con TRUE POS</Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <CssBaseline />
      <CardView card={card} cardUrl={`${window.location.origin}/${slug}/mi/${token}`} welcome={welcome} slug={slug} token={token} notice={notice} />
    </>
  );
};

export default LoyaltyCard;
